import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { RobocloudDTO } from '@robocorp/robocloud-common/lib/types';
import {
  formatErrorPayload,
  UseApiMutationOptions,
  UseApiQueryOptions,
  validateApiResponse,
} from '../utils';
import { activityService } from '~/services/processAPI';
import { useCurrentWorkspaceQueryKey } from '../utils/useQueryKey';
import { secretService } from '~/services/secretsAPI';
import {
  generateRSAKeyPair,
  exportRSAPublicKey,
  encrypt,
  decrypt,
} from '~/utils/transitEncryptionHelper';
import { Secret } from '~/types/resources';
import { formatArrToJson, parseJsonString } from '~/utils/formatKeyValuePairHelper';

const ROOT_KEY = 'secrets';
const getRootQueryKey = () => [ROOT_KEY];
export const getItemQueryKey = (key: string) => [ROOT_KEY, key];

export const useSecretsQuery = (
  options?: UseApiQueryOptions<typeof secretService.getSecretListItems>,
) => {
  const dispatch = useDispatch();

  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();
  return useQuery(
    queryKeyFormatter(getRootQueryKey()),
    async () => validateApiResponse(await secretService.getSecretListItems({ workspaceId })).data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useSecretQuery = (
  { name }: { name: string },
  options?: UseApiQueryOptions<typeof secretService.getSecretByName, Secret>,
) => {
  const dispatch = useDispatch();

  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();
  return useQuery(
    queryKeyFormatter(getItemQueryKey(name)),
    async () => {
      const keyPair: CryptoKeyPair = await generateRSAKeyPair();
      const publicKey = await exportRSAPublicKey(keyPair.publicKey);
      const encryptionScheme = RobocloudDTO.EncryptionSchemes.V2;
      const { data } = validateApiResponse(
        await secretService.getSecretByName({ workspaceId, name, publicKey, encryptionScheme }),
      );

      if (data) {
        const secret = await decrypt(data, keyPair.privateKey);
        const value: Secret['value'] =
          secret.value !== undefined ? parseJsonString(secret.value) : secret.value;
        return { ...secret, value };
      }
      return data;
    },
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useCreateSecretMutation = <T extends typeof secretService.putSecretByName>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();
  return useMutation(
    async (secret: Secret) => {
      const formattedValue = secret.value && formatArrToJson(secret.value);
      const { value, encryption } = await encrypt({ workspaceId }, formattedValue);

      const data = {
        ...secret,
        value,
        encryption,
      } as RobocloudDTO.EncryptedSecretV2;

      return validateApiResponse(
        await secretService.putSecretByName({
          workspaceId,
          name: secret.name,
          updateDetails: data,
        }),
      ).data;
    },
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      onSuccess: () => queryClient.invalidateQueries(queryKeyFormatter(getRootQueryKey())),
      ...options,
    },
  );
};

export const useUpdateSecretMutation = <T extends typeof secretService.putSecretByName>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();
  return useMutation(
    async ({ name, secret }: { name: string; secret: Secret }) => {
      const formattedValue = secret.value && formatArrToJson(secret.value);
      const { value, encryption } = await encrypt({ workspaceId }, formattedValue);

      const data = {
        ...secret,
        value,
        encryption,
      } as RobocloudDTO.EncryptedSecretV2;

      return validateApiResponse(
        await secretService.putSecretByName({ workspaceId, name, updateDetails: data }),
      ).data;
    },
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      onSuccess: (_, { name }) => {
        queryClient.invalidateQueries(queryKeyFormatter(getRootQueryKey()));
        queryClient.invalidateQueries(queryKeyFormatter(getItemQueryKey(name)));
      },
      ...options,
    },
  );
};

export const useDeleteSecretMutation = (
  options?: UseApiMutationOptions<typeof activityService.deleteActivity>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();
  return useMutation(
    async ({ name }: { name: string }) =>
      validateApiResponse(await secretService.deleteSecretByName({ workspaceId, name })).data,
    {
      onSuccess: (_, { name }) => {
        queryClient.invalidateQueries(queryKeyFormatter(getRootQueryKey()));
        queryClient.invalidateQueries(queryKeyFormatter(getItemQueryKey(name)));
      },
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};
